import { zodResolver } from '@hookform/resolvers/zod';
import { useToastController } from '@tamagui/toast';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import { Form, YStack } from 'tamagui';
import { z } from 'zod';

import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { ControlledDateInput } from '../../components/controlled/ControlledDateInput';
import { ControlledDateTextInput } from '../../components/controlled/ControlledDateTextInput';
import { ControlledDollarInput } from '../../components/controlled/ControlledDollarInput';
import {
    ControlledInput,
    ControlledInputTrigger,
    ControlledMultilineInput,
    USE_FORM_MODE_DEFAULTS,
} from '../../components/controlled/ControlledInput';
import { ControlledPercentInput } from '../../components/controlled/ControlledPercentInput';
import { ControlledPhoneNumberInput } from '../../components/controlled/ControlledPhoneNumberInput';
import { ControlledSSNInput } from '../../components/controlled/ControlledSSNInput';

const exampleSchema = z.object({
    textInput: z.string().min(1, 'Required input'),
    multilineInput: z.string().min(1, 'Required input'),
    inputTriggerText: z.string().min(1, 'Required input'),
    phoneNumberInput: z.string().min(1, 'Required input'),
    ssnInput: z.string().min(1, 'Required input'),
    dollarInput: z.string().min(1, 'Required input'),
    percentInput: z.string().min(1, 'Required input'),
    dateInput: z.string().min(1, 'Required input'),
    dateTextInput: z.string().min(1, 'Required input'),
});

type ExampleSchema = z.infer<typeof exampleSchema>;

export const ControlledInputs = () => {
    const toast = useToastController();

    const {
        register,
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<ExampleSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(exampleSchema),
        defaultValues: {
            textInput: '',
            multilineInput: '',
            inputTriggerText: '',
            phoneNumberInput: '',
            ssnInput: '',
            dollarInput: '',
            percentInput: '',
            dateInput: '',
            dateTextInput: '',
        },
    });

    const onSubmit: SubmitHandler<ExampleSchema> = ({
        textInput,
    }: ExampleSchema) => {
        toast.show(`Yay! ${textInput} was submitted`);
    };

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <YStack gap={12}>
                <ControlledInput
                    label="Example controlled text input"
                    name="textInput"
                    control={control}
                    register={register}
                    errors={errors}
                    onSubmitEditing={handleSubmit(onSubmit)}
                />
                <ControlledMultilineInput
                    label="Example multi-line controlled text input"
                    name="multilineInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledInputTrigger
                    label="Example controlled input trigger"
                    name="inputTriggerText"
                    control={control}
                    register={register}
                    errors={errors}
                    onTrigger={() => {
                        setValue(
                            'inputTriggerText',
                            'Example input trigger value',
                        );
                    }}
                />
                <ControlledPhoneNumberInput
                    label="Example controlled phone number input"
                    name="phoneNumberInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledSSNInput
                    label="Example controlled SSN input"
                    name="ssnInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledDollarInput
                    label="Example controlled dollar input"
                    name="dollarInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledPercentInput
                    label="Example controlled percent input"
                    hint="The amount of income you estimate you pay in bills each month."
                    name="percentInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledDateInput
                    label="Example controlled date input"
                    name="dateInput"
                    control={control}
                    register={register}
                    errors={errors}
                />
                <ControlledDateTextInput
                    label="Example controlled date text input"
                    name="dateTextInput"
                    control={control}
                    register={register}
                    errors={errors}
                    onSubmitEditing={() => {
                        Keyboard.dismiss();
                    }}
                />
            </YStack>
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                text="Submit"
                buttonProps={{ marginTop: 12 }}
                onPress={handleSubmit(onSubmit)}
                disabled={!isValid}
            />
        </Form>
    );
};
